import React from "react";
import { useHistory } from "react-router-dom";
import makeStyles from "@mui/styles/makeStyles";
import Typography from "@mui/material/Typography";
// Limitless Components
import { useTranslation, useResources } from "@limitless/common/hooks";
import { Header, TimeSinceFooter } from "@limitless/customer/components";
import CreateMessageForm from "components/create-message-form/CreateMessageForm";
import Toast from "@limitless/customer/components/toast/Toast";
import { useCreateMessage } from "hooks/create-message-hook/CreateMessageHook";
import { useCustomerAutoSubmit } from "../hooks/customer-auto-submit/CustomerAutoSubmitHook";
import { useCustomerContext, useCustomerHistoryContext, useCustomerMessageContext } from "@limitless/customer/hooks";
import { ClientContext } from "@limitless/common/utils";
import { usePageViewRecorder } from "@limitless/analytics/hooks/AnalyticsPageViewRecorderHook";

import Dialog from "@mui/material/Dialog";
import MessageVerificationForm from "../components/create-message-form/MessageVerificationForm";
import MessageVerifyDialog from "../components/create-message-form/MessageVerifyDialog";
import { CREATE, DONOTCREATE, VERIFY } from "@limitless/libraries/src/portal/utils/ReContactMessageConstant";

const useStyles = makeStyles({
    loadingContainer: {
        display: "flex",
        flex: 1,
        justifyContent: "center",
        alignItems: "center"
    },
    fullScreenPaper: {
        margin: 0
    },
    headerContent: {
        display: "flex",
        width: "367px",
        height: "36px",
        flexDirection: "column",
        justifyContent: "center"
    },
    paper: {
        margin: 8,
        display: "block",
        background: "unset",
        overflow: "hidden"
    }
});

const CreateMessageScreen = () => {
    const { toggleAppOpen } = useCreateMessage();
    const {
        resources: { llmConfig }
    } = useResources();
    const { isReturningCustomer } = useCustomerContext();
    const { history } = useCustomerHistoryContext();
    const navigateHistory = useHistory();
    //evaluating is so we don't prematurely flash the history screen before the converation screen
    const { evaluating, autoSubmitLoading, isSubject } = useCustomerAutoSubmit();
    const { dialogOpen, setDialogOpen, isMessageOpen } = useCustomerMessageContext();

    const classes = useStyles();
    const { t } = useTranslation();
    const { activeMessages, previousMessages } = history;
    const newMessageRule = llmConfig?.attribute?.data_new_message_rules_new_message;
    const reContactMessageRule = llmConfig?.attribute?.data_new_message_rules_recontact_llm;

    usePageViewRecorder("CreateMessageScreen");
    const [reContactMessageVerify, setReContactMessageVerify] = React.useState(false);

    React.useEffect(() => {
        //this would fire if it's been rehydrated - validates the user as a) our code could changed b) could've been manually changed
        if (!evaluating && !autoSubmitLoading) {
            navigateHistory.push(
                ClientContext._newConversationMode !== "standard" &&
                    (activeMessages.length > 0 || previousMessages.length > 0)
                    ? "/history"
                    : isReturningCustomer() && isSubject && "/newMessage"
            );
        }
    }, [evaluating]);

    const isAutoSubmitting = evaluating || autoSubmitLoading;

    const reContactDialogComponent = React.useMemo(() => {
        if ([CREATE, DONOTCREATE, VERIFY].includes(reContactMessageRule) && !reContactMessageVerify && isMessageOpen) {
            return (
                <MessageVerifyDialog
                    reContactMessageRule={reContactMessageRule}
                    setReContactMessageVerify={setReContactMessageVerify}
                />
            );
        }

        //verify code from email
        if (
            newMessageRule === VERIFY ||
            (reContactMessageVerify && reContactMessageRule === VERIFY && !isMessageOpen)
        ) {
            return <MessageVerificationForm llmConfig={llmConfig} />;
        }

        return null;
    }, [
        newMessageRule,
        reContactMessageRule,
        reContactMessageVerify,
        llmConfig,
        setDialogOpen,
        setReContactMessageVerify,
        isMessageOpen
    ]);

    return (
        <div className="llm-view-container">
            <Header title="Get Help" onCloseHandle={toggleAppOpen} />
            <Toast />

            {isAutoSubmitting ? (
                <div className={classes.loadingContainer}>
                    <Typography>{t("Submitting question..")}</Typography>
                </div>
            ) : (
                <>
                    <CreateMessageForm llmConfig={llmConfig} />

                    <Dialog
                        open={dialogOpen}
                        disablePortal
                        transitionDuration={{ enter: 500 }}
                        BackdropProps={{ classes: { root: classes.backdrop } }}
                        style={{ position: "absolute" }}
                        classes={{
                            paper: classes.paper
                        }}
                        onClose={(event, reason) => {
                            if (reason === "backdropClick") {
                                setDialogOpen(!dialogOpen);
                            }
                        }}
                    >
                        {reContactDialogComponent}
                    </Dialog>
                </>
            )}
            <TimeSinceFooter />
        </div>
    );
};

export default CreateMessageScreen;
